import { DEFAULT_DATE_FORMAT } from '@sophocles/app/constants';
import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import {
  InternationalMeterReading,
  internationalMeterReadingSchema,
} from '@sophocles/entities/InternationalMeterReading/internationalMeterReadingRecord';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_INTERNATIONAL_METER_READINGS = 'RECEIVE_INTERNATIONAL_METER_READINGS';
function receiveInternationalMeterReadings(json) {
  return {
    type: RECEIVE_INTERNATIONAL_METER_READINGS,
    internationalMeterReadings: mapListToRecords(json, InternationalMeterReading),
  };
}

/* Request Actions */
// starter request
export const REQUEST_INTERNATIONAL_METER_READING = 'REQUEST_INTERNATIONAL_METER_READING';
function requestInternationalMeterReading(internationalMeterReading_id) {
  return {
    type: REQUEST_INTERNATIONAL_METER_READING,
    internationalMeterReading_id,
  };
}

export const REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE = 'REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE';
function requestInternationalMeterReadingForSite(site_id) {
  return {
    type: REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE,
    site_id,
  };
}

export const REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE_WITH_RANGE =
  'REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE_WITH_RANGE';
function requestInternationalMeterReadingForSiteWithRange(site_id, to, from) {
  return {
    type: REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE_WITH_RANGE,
    site_id,
    to,
    from,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchInternationalMeterReading(internationalMeterReading_id) {
  return dispatch => {
    dispatch(requestInternationalMeterReading(internationalMeterReading_id));
    return apiClient
      .get(`/internationalmeterreadings/${internationalMeterReading_id}/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data, 'internationalMeterReadings', internationalMeterReadingSchema))
      .then(json => dispatch(receiveInternationalMeterReadings(json.entities.internationalMeterReadings)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_INTERNATIONAL_METER_READING)));
  };
}

export function fetchInternationalMeterReadingForSite(site_id) {
  return dispatch => {
    dispatch(requestInternationalMeterReadingForSite(site_id));
    return apiClient
      .get('/internationalmeterreadings/', { params: { site: site_id } })
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(response.data.results, 'internationalMeterReadings', internationalMeterReadingSchema),
      )
      .then(json => dispatch(receiveInternationalMeterReadings(json.entities.internationalMeterReadings)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE)));
  };
}

export function fetchInternationalMeterReadingForSiteWithDateRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));
  return dispatch => {
    dispatch(requestInternationalMeterReadingForSite(site_id));
    return apiClient
      .get('/internationalmeterreadings/', {
        params: {
          site: site_id,
          timestamp__gte: format(fromTS, DEFAULT_DATE_FORMAT),
          timestamp__lte: format(toTS, DEFAULT_DATE_FORMAT),
        },
      })
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(response.data.results, 'internationalMeterReadings', internationalMeterReadingSchema),
      )
      .then(json => dispatch(receiveInternationalMeterReadings(json.entities.internationalMeterReadings)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_INTERNATIONAL_METER_READINGS_FOR_SITE)));
  };
}

/* Edit Actions */
export const EDIT_INTERNATIONAL_METER_READING = 'EDIT_INTERNATIONAL_METER_READING';
export function editInternationalMeterReading(internationalMeterReading_id) {
  return {
    type: EDIT_INTERNATIONAL_METER_READING,
    internationalMeterReading: internationalMeterReading_id,
  };
}

export function patchInternationalMeterReading(internationalMeterReading_id, values) {
  return dispatch => {
    dispatch(editInternationalMeterReading(internationalMeterReading_id));
    return apiClient
      .patch(`/internationalmeterreadings/${internationalMeterReading_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, internationalMeterReadingSchema))
      .then(json => dispatch(receiveInternationalMeterReadings(json.entities.internationalMeterReadings)))
      .catch(error => dispatch(genericErrorAction(error, EDIT_INTERNATIONAL_METER_READING)));
  };
}

/* Create Actions */
export const CREATE_INTERNATIONAL_METER_READING = 'CREATE_INTERNATIONAL_METER_READING';
function sayCreateInternationalMeterReading(values) {
  return {
    type: CREATE_INTERNATIONAL_METER_READING,
    values: values,
  };
}

export function createInternationalMeterReading(values) {
  return dispatch => {
    dispatch(sayCreateInternationalMeterReading(values));
    return apiClient
      .post(`/internationalmeterreadings/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, internationalMeterReadingSchema))
      .then(json => dispatch(receiveInternationalMeterReadings(json.entities.internationalMeterReadings)))
      .catch(error => dispatch(genericErrorAction(error, CREATE_INTERNATIONAL_METER_READING)));
  };
}

/* Delete Actions */
export const DELETE_INTERNATIONAL_METER_READING = 'DELETE_INTERNATIONAL_METER_READING';
function sayDeleteInternationalMeterReading(internationalMeterReading_id) {
  return {
    type: DELETE_INTERNATIONAL_METER_READING,
    internationalMeterReading: internationalMeterReading_id,
  };
}

export function deleteInternationalMeterReading(internationalMeterReading_id) {
  return dispatch =>
    apiClient
      .delete(`/internationalmeterreadings/${internationalMeterReading_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteInternationalMeterReading(internationalMeterReading_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_INTERNATIONAL_METER_READING)));
}
